import React, { type FunctionComponent, useState } from 'react'
import { hasEditUserPermission } from '../../../../services/permission.service'
import { Dialog } from '../../../common/Dialog'
import { logError } from '@tomra/datadog-browser-logging'
import { API_HOST, authenticatedFetchData } from '../../../../lib'
import { Button } from '@tomra/design-system-react/button'

type Props = {
  userInfo: UserInfoType
}

export const UserPhoneNumber: FunctionComponent<Props> = ({ userInfo }) => {
  const { id, email, appContext, phoneNumber } = userInfo

  const [status, setStatus] = useState<RequestStatusType>('idle')
  const [showResetPhoneNumberDialog, setShowResetPhoneNumberDialog] = useState(false)
  const [userPhoneNumber, setUserPhoneNumber] = useState<string | null>(phoneNumber)

  async function resetPhoneNumberForUser() {
    try {
      setStatus('pending')

      await authenticatedFetchData(`${API_HOST}/mytomra/v1.0/hotlinetool/reset-user-phone-number?userId=${id}`, {
        method: 'POST'
      }).run()

      setUserPhoneNumber(null)
      setStatus('success')
    } catch (error: any) {
      logError(new Error('Failed to reset phone number'), error)
      setStatus('failure')
    }
  }

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 items-end gap-4">
        <dl className="col-span-2">
          <dt id="phoneNumber" className="text-gray-600">
            Phone number
          </dt>
          <dd aria-labelledby="phoneNumber">{userPhoneNumber || 'Not provided'}</dd>
        </dl>

        {hasEditUserPermission() && userPhoneNumber ? (
          <Button
            className="px-3 py-1 text-xs"
            onClick={() => {
              setStatus('idle')
              setShowResetPhoneNumberDialog(true)
            }}
          >
            Reset
          </Button>
        ) : null}
      </div>

      {showResetPhoneNumberDialog && (
        <Dialog
          ariaLabel="Reset phone number"
          onClose={() => {
            setShowResetPhoneNumberDialog(false)
          }}
        >
          <div className="rounded-2xl bg-white shadow-md dark:bg-gray-900 p-6 flex flex-col gap-y-4 pt-9 min-w-30 max-w-300">
            <h1 className="text-xl">Reset phone number</h1>

            {status === 'success' ? (
              <>
                <p>
                  The phone number was reset for <b>{email}</b>.
                </p>

                <div className="rounded-lg border-l-[3px] border-amber-400 bg-white p-4 text-left shadow-md dark:bg-gray-900">
                  The user will have to log out and back in for the reset to take effect.
                </div>

                <div className="flex items-center justify-end space-x-4">
                  <Button onClick={() => setShowResetPhoneNumberDialog(false)}>Close</Button>
                </div>
              </>
            ) : (
              <>
                <p>
                  This will reset the phone number for <b>{email}</b> user account in <b>{appContext}</b>.
                </p>
                <div className="rounded-lg border-l-[3px] border-amber-400 bg-white p-4 text-left shadow-md dark:bg-gray-900">
                  The user will have to log out and back in for the reset to take effect.
                </div>

                {status === 'failure' && <div className="text-rose-700">Something went wrong. Please try again.</div>}

                <div className="flex items-center justify-end space-x-4">
                  <Button onClick={() => setShowResetPhoneNumberDialog(false)}>Cancel</Button>
                  <Button variant="destructive" onClick={resetPhoneNumberForUser}>
                    {status === 'pending' ? <div className="loadingSpinner" /> : 'Reset phone number'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </Dialog>
      )}
    </>
  )
}
